// features/superAdmin/superAdminSlice.ts

import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

export interface User {
    id: number;
    name: string;
    email: string;
    role: 'internal_operator' | 'customer_admin';
    tenantId: number | null;
}

export interface Tenant {
    id: number;
    name: string;
    logo: string;
    primaryColor: string;
    secondaryColor: string;
    domain: string;
}

export interface Customer {
    id: string;
    companyName: string;
    adminName: string;
    adminEmail: string;
    configuration: {
        enableAnalytics: boolean;
        enableApiAccess: boolean;
        subscriptionTier: string;
        maxUsers: number;
    };
    createdAt: Date;
}

export interface Activity {
    id: string;
    action: string;
    user: string;
    timestamp: Date;
}

interface SuperAdminState {
    users: User[];
    tenants: Tenant[];
    customers: Customer[];
    activities: Activity[];
    loading: boolean;
    error: string | null;
}

const initialState: SuperAdminState = {
    users: [
        { id: 1, name: 'John Operator', email: 'john@internal.com', role: 'internal_operator', tenantId: null },
        { id: 2, name: 'Alice Admin', email: 'alice@tenantA.com', role: 'customer_admin', tenantId: 1 },
    ],
    tenants: [
        {
            id: 1,
            name: 'Tenant A',
            logo: 'https://via.placeholder.com/150',
            primaryColor: '#3B82F6',
            secondaryColor: '#1D4ED8',
            domain: 'tenanta.platform.com'
        },
        {
            id: 2,
            name: 'Tenant B',
            logo: 'https://via.placeholder.com/150',
            primaryColor: '#10B981',
            secondaryColor: '#059669',
            domain: 'tenantb.platform.com'
        },
    ],
    customers: [],
    activities: [],
    loading: false,
    error: null,
};

export const createCustomer = createAsyncThunk(
    'superAdmin/createCustomer',
    async (customerData: any, { rejectWithValue }) => {
        try {
            await new Promise(resolve => setTimeout(resolve, 1500));
            const newCustomer = {
                id: Date.now().toString(),
                ...customerData,
                createdAt: new Date(),
            };
            const activity = {
                id: Date.now().toString(),
                action: `Created customer "${customerData.companyName}"`,
                user: customerData.adminEmail,
                timestamp: new Date(),
            };
            return { customer: newCustomer, activity };
        } catch (error) {
            return rejectWithValue('Failed to create customer');
        }
    }
);

const superAdminSlice = createSlice({
    name: 'superAdmin',
    initialState,
    reducers: {
        addUser: (state, action: PayloadAction<Omit<User, 'id'>>) => {
            const newUser = { ...action.payload, id: Date.now() };
            state.users.push(newUser);
        },
        updateUser: (state, action: PayloadAction<User>) => {
            const index = state.users.findIndex(u => u.id === action.payload.id);
            if (index !== -1) state.users[index] = action.payload;
        },
        deleteUser: (state, action: PayloadAction<number>) => {
            state.users = state.users.filter(u => u.id !== action.payload);
        },
        addTenant: (state, action: PayloadAction<Omit<Tenant, 'id'>>) => {
            const newTenant = { ...action.payload, id: Date.now() };
            state.tenants.push(newTenant);
        },
        updateTenant: (state, action: PayloadAction<Tenant>) => {
            const index = state.tenants.findIndex(t => t.id === action.payload.id);
            if (index !== -1) state.tenants[index] = action.payload;
        },
        deleteTenant: (state, action: PayloadAction<number>) => {
            state.tenants = state.tenants.filter(t => t.id !== action.payload);
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createCustomer.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createCustomer.fulfilled, (state, action) => {
                state.loading = false;
                state.customers.push(action.payload.customer);
                state.activities.unshift(action.payload.activity);
            })
            .addCase(createCustomer.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const {
    addUser,
    updateUser,
    deleteUser,
    addTenant,
    updateTenant,
    deleteTenant,
    clearError
} = superAdminSlice.actions;

export default superAdminSlice.reducer;